import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Discrepancy, SharePackage, SharePickup } from "../api/types";
import { PickupCard, type PickupCardInput } from "../components/PickupCard";
import { CardBoundary } from "../components/CardBoundary";
import { SharingCentre } from "../components/SharingCentre";
import { GeneralNotesList } from "../components/GeneralNotes";
import { GuideTabs } from "../components/GuideTabs";
import { TimecodeToggle, useTimecodeBasis } from "../components/TimecodeToggle";
import { Icon } from "../components/icons";
import { headlineFor } from "../lib/reportStore";
import { fallbackNote, formatTimecode, type TimecodeBasis } from "../lib/pickupContext";

// Public share card input: the server already bundled a bounded context window
// (token slice + LOCAL flag indices) + commentary, so "show more" is capped to
// the shared snippet — the whole manuscript is never exposed.
function sharedInput(it: SharePickup, basis: TimecodeBasis): PickupCardInput {
  const w = it.window;
  return {
    flag_key: it.flag_key,
    addressed: it.addressed,
    audio_filename: it.audio_filename,
    page: it.page,
    flag_type: it.flag_type || "",
    category: it.category,
    title: it.title || headlineFor({ type: it.flag_type ?? "", script_text: it.script_text, heard_text: it.heard_text } as Discrepancy),
    comment: it.comment || fallbackNote(it.flag_type || "", it.script_text, it.heard_text, it.start_time, it.end_time),
    timecode: formatTimecode(it.start_time, it.chapter_offset, basis),
    audio_url: it.audio_url,
    tokens: w?.tokens ?? null,
    flagFirst: w?.flag_first,
    flagLast: w?.flag_last,
    heardText: w?.heard_text ?? it.heard_text,
    context_before: it.context_before,
    context_after: it.context_after,
    fallbackFlag: it.flag_type === "added" ? it.heard_text : it.script_text || it.heard_text,
    start_time: it.start_time,
    end_time: it.end_time,
  };
}

/**
 * SharedPackage — the public, no-login Pickups Package rendered from a share
 * token (route `/share/:token`). Fetches the self-contained package payload and
 * renders the same cards as the owner view; a viewer can audition each fragment
 * and tick "addressed", nothing more. No app chrome, no account required.
 */
export function SharedPackage() {
  const { token } = useParams();
  const qc = useQueryClient();
  const key = ["share-package", token];

  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => api<SharePackage>(`/api/share/${token}/package`),
    enabled: !!token,
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  });

  const addressedM = useMutation({
    mutationFn: (v: { flag_key: string; addressed: boolean }) =>
      api(`/api/share/${token}/pickups/addressed`, { method: "POST", body: JSON.stringify(v) }),
    onMutate: (v) => {
      qc.setQueryData<SharePackage>(key, (cur) =>
        cur
          ? {
              ...cur,
              pickups: cur.pickups.map((p) =>
                p.flag_key === v.flag_key ? { ...p, addressed: v.addressed } : p,
              ),
            }
          : cur,
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  const list = data?.pickups ?? [];
  const addressed = list.filter((p) => p.addressed).length;
  const [basis, setBasis] = useTimecodeBasis();

  return (
    <div className="pickups-standalone">
      <header className="pk-topbar">
        <a className="pk-brand" href="/" target="_blank" rel="noopener noreferrer" title="About Mockingbird">
          <Icon name="logo" size={22} />
          <span className="pk-brand-name">Mockingbird</span>
        </a>
        {data?.guide_enabled && token && (
          <GuideTabs active="pickups" pickupsTo={`/share/${token}`} guideTo={`/share/${token}/guide`} />
        )}
      </header>

      <div className="page pickups-page">
        {isError ? (
          <div className="empty-state">
            <h3>This link isn’t available</h3>
            <p>The share link may have been turned off, or the address is incorrect.</p>
          </div>
        ) : isLoading ? (
          <div className="empty-state"><p>Loading…</p></div>
        ) : (
          <>
            <div className="page-head pk-head">
              <div>
                <div className="pk-eyebrow">Pickups Package</div>
                <h1 className="pk-title">{data?.project_name ?? "…"}</h1>
                <div className="sub">
                  {list.length} pickup{list.length === 1 ? "" : "s"}
                  {list.length > 0 && ` · ${addressed}/${list.length} addressed`}
                </div>
              </div>
              {token && (
                <SharingCentre
                  exportBase={`/api/share/${token}/export`}
                  copyUrl={async () => window.location.href}
                  title={data?.project_name ?? ""}
                  pickupCount={list.length}
                  basis={basis}
                />
              )}
            </div>
            <div className="pk-banner">
              <span className="dot ok" /> Shared read-only — audition each fragment and tick the ones you’ve re-recorded.
            </div>

            <GeneralNotesList notes={data?.notes ?? []} />

            {list.length === 0 ? (
              <div className="empty-state">
                <h3>No pickups yet</h3>
                <p>This package doesn’t have any picked-up flags to work through yet.</p>
              </div>
            ) : (
              <>
                <div className="pk-toolbar">
                  <TimecodeToggle value={basis} onChange={setBasis} />
                </div>
                <div className="pickup-list">
                  {list.map((p, i) => {
                    const input = sharedInput(p, basis);
                    const onAddressed = (v: boolean) => addressedM.mutate({ flag_key: p.flag_key, addressed: v });
                    return (
                      <CardBoundary
                        key={p.flag_key}
                        fallback={<PickupCard index={i} total={list.length} input={{ ...input, tokens: null }} onAddressed={onAddressed} />}
                      >
                        <PickupCard index={i} total={list.length} input={input} onAddressed={onAddressed} />
                      </CardBoundary>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
